import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ReactECharts from 'echarts-for-react';

const { FiHome, FiDollarSign, FiTrendingDown, FiCalendar, FiX, FiPercent, FiTarget, FiCheck, FiClock } = FiIcons;

function HomeLoanCalculator({ onClose, onAddAsGoal }) {
  const [formData, setFormData] = useState({
    outstandingBalance: 5000000,
    interestRate: 8.5,
    remainingTenure: 180, // in months
    targetYears: 10, // target to clear in years
    mutualFundReturn: 12, // Expected mutual fund returns
    sipStartYear: 5, // When to start SIP investment (in years from now)
    sipDuration: 5, // How many years to invest in SIP
  });

  const [showAddGoal, setShowAddGoal] = useState(false);
  const [goalFormData, setGoalFormData] = useState({
    investmentType: 'sip', // 'sip' or 'extraEMI'
    timeHorizon: 10,
  });

  // Calculate current EMI based on loan parameters
  const calculateEMI = (principal, rate, tenure) => {
    const monthlyRate = rate / 100 / 12;
    if (monthlyRate === 0) {
      return principal / tenure;
    }
    return (principal * monthlyRate * Math.pow(1 + monthlyRate, tenure)) / (Math.pow(1 + monthlyRate, tenure) - 1);
  };

  // Auto-calculate current EMI whenever inputs change
  const currentEMI = useMemo(() => {
    return calculateEMI(
      formData.outstandingBalance,
      formData.interestRate,
      formData.remainingTenure
    );
  }, [formData.outstandingBalance, formData.interestRate, formData.remainingTenure]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  const handleGoalFormChange = (field, value) => {
    setGoalFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(value);
  };

  // Calculate loan details
  const calculations = useMemo(() => {
    const monthlyRate = formData.interestRate / 100 / 12;
    const targetMonths = formData.targetYears * 12;

    // Calculate required extra payment to clear in target years
    let requiredExtraPayment = 0;
    if (targetMonths < formData.remainingTenure) {
      // Calculate EMI needed to clear loan in target months
      const requiredEMI = formData.outstandingBalance * (monthlyRate * Math.pow(1 + monthlyRate, targetMonths)) / (Math.pow(1 + monthlyRate, targetMonths) - 1);
      requiredExtraPayment = Math.max(0, requiredEMI - currentEMI);
    }

    // Calculate standard scenario (current EMI)
    let standardBalance = formData.outstandingBalance;
    let standardTotalPrincipal = 0;
    let standardTotalInterest = 0;
    const standardSchedule = [];

    for (let month = 1; month <= formData.remainingTenure && standardBalance > 0; month++) {
      const interestPayment = standardBalance * monthlyRate;
      const principalPayment = Math.min(currentEMI - interestPayment, standardBalance);
      standardBalance -= principalPayment;
      standardTotalPrincipal += principalPayment;
      standardTotalInterest += interestPayment;

      if (month % 12 === 0 || standardBalance <= 0) {
        standardSchedule.push({
          year: Math.ceil(month / 12),
          month: month,
          balance: Math.max(0, standardBalance),
          principal: standardTotalPrincipal,
          interest: standardTotalInterest,
          emi: currentEMI
        });
      }
    }

    // Calculate accelerated scenario with extra payment
    const extraPaymentAmount = requiredExtraPayment;
    let acceleratedBalance = formData.outstandingBalance;
    let acceleratedTotalPrincipal = 0;
    let acceleratedTotalInterest = 0;
    const acceleratedSchedule = [];

    for (let month = 1; month <= formData.remainingTenure && acceleratedBalance > 0; month++) {
      const totalPayment = currentEMI + extraPaymentAmount;
      const interestPayment = acceleratedBalance * monthlyRate;
      const principalPayment = Math.min(totalPayment - interestPayment, acceleratedBalance);
      acceleratedBalance -= principalPayment;
      acceleratedTotalPrincipal += principalPayment;
      acceleratedTotalInterest += interestPayment;

      if (month % 12 === 0 || acceleratedBalance <= 0) {
        acceleratedSchedule.push({
          year: Math.ceil(month / 12),
          month: month,
          balance: Math.max(0, acceleratedBalance),
          principal: acceleratedTotalPrincipal,
          interest: acceleratedTotalInterest,
          emi: totalPayment,
          extraPayment: extraPaymentAmount
        });
      }

      if (acceleratedBalance <= 0) break;
    }

    const standardTenureMonths = standardSchedule[standardSchedule.length - 1]?.month || formData.remainingTenure;
    const acceleratedTenureMonths = acceleratedSchedule[acceleratedSchedule.length - 1]?.month || formData.remainingTenure;

    // Calculate outstanding balance at SIP start year
    const sipStartMonths = formData.sipStartYear * 12;
    let outstandingAtSIPStart = formData.outstandingBalance;
    for (let month = 1; month <= sipStartMonths && outstandingAtSIPStart > 0; month++) {
      const interestPayment = outstandingAtSIPStart * monthlyRate;
      const principalPayment = Math.min(currentEMI - interestPayment, outstandingAtSIPStart);
      outstandingAtSIPStart -= principalPayment;
    }

    // Calculate SIP required to reach outstanding balance at target year
    const sipMonthlyReturn = formData.mutualFundReturn / 100 / 12;
    const sipMonths = formData.sipDuration * 12;

    // Calculate outstanding at target year (SIP start + SIP duration)
    const targetMonthsForSIP = (formData.sipStartYear + formData.sipDuration) * 12;
    let outstandingAtTarget = formData.outstandingBalance;
    for (let month = 1; month <= targetMonthsForSIP && outstandingAtTarget > 0; month++) {
      const interestPayment = outstandingAtTarget * monthlyRate;
      const principalPayment = Math.min(currentEMI - interestPayment, outstandingAtTarget);
      outstandingAtTarget -= principalPayment;
    }

    // Calculate SIP needed to accumulate outstanding balance at target
    let sipRequired = 0;
    if (sipMonthlyReturn > 0) {
      sipRequired = outstandingAtTarget / ((Math.pow(1 + sipMonthlyReturn, sipMonths) - 1) / sipMonthlyReturn);
    } else {
      sipRequired = outstandingAtTarget / sipMonths;
    }

    // Calculate lumpsum required to clear outstanding at target
    const lumpsumRequired = outstandingAtTarget / Math.pow(1 + formData.mutualFundReturn / 100, formData.sipDuration);

    // LTCG Tax Calculation for SIP/Lumpsum Investment
    // Investment period is sipDuration years (> 1 year, so LTCG applies)
    const sipTotalInvestment = sipRequired * 12 * formData.sipDuration;
    const sipFutureValue = outstandingAtTarget;
    const sipProfit = sipFutureValue - sipTotalInvestment;
    const sipTaxableGains = Math.max(0, sipProfit - 125000); // ₹1.25L exemption
    const sipLTCGTax = sipTaxableGains * 0.125; // 12.5% LTCG tax
    const sipPostTaxValue = sipFutureValue - sipLTCGTax;

    const lumpsumProfit = outstandingAtTarget - lumpsumRequired;
    const lumpsumTaxableGains = Math.max(0, lumpsumProfit - 125000);
    const lumpsumLTCGTax = lumpsumTaxableGains * 0.125;
    const lumpsumPostTaxValue = outstandingAtTarget - lumpsumLTCGTax;

    return {
      requiredExtraPayment: Math.round(requiredExtraPayment),
      currentEMI: Math.round(currentEMI),

      // Standard scenario
      standardTotalPayment: standardTotalPrincipal + standardTotalInterest,
      standardTotalPrincipal,
      standardTotalInterest,
      standardTenureMonths,
      standardTenureYears: (standardTenureMonths / 12).toFixed(1),
      standardSchedule,
      standardAnnualEMI: Math.round(currentEMI * 12),

      // Accelerated scenario
      acceleratedTotalPayment: acceleratedTotalPrincipal + acceleratedTotalInterest,
      acceleratedTotalPrincipal,
      acceleratedTotalInterest,
      acceleratedTenureMonths,
      acceleratedTenureYears: (acceleratedTenureMonths / 12).toFixed(1),
      acceleratedSchedule,
      acceleratedAnnualEMI: Math.round((currentEMI + requiredExtraPayment) * 12),

      // Savings
      tenureSaved: standardTenureMonths - acceleratedTenureMonths,
      interestSaved: standardTotalInterest - acceleratedTotalInterest,
      totalSaved: (standardTotalPrincipal + standardTotalInterest) - (acceleratedTotalPrincipal + acceleratedTotalInterest),

      // SIP Investment Strategy
      outstandingAtSIPStart: Math.round(outstandingAtSIPStart),
      outstandingAtTarget: Math.round(outstandingAtTarget),
      sipRequired: Math.round(sipRequired),
      lumpsumRequired: Math.round(lumpsumRequired),
      totalSIPInvestment: Math.round(sipRequired * 12 * formData.sipDuration),
      sipReturns: Math.round(outstandingAtTarget - (sipRequired * 12 * formData.sipDuration)),

      // LTCG Tax Calculations
      sipProfit: Math.round(sipProfit),
      sipTaxableGains: Math.round(sipTaxableGains),
      sipLTCGTax: Math.round(sipLTCGTax),
      sipPostTaxValue: Math.round(sipPostTaxValue),
      lumpsumProfit: Math.round(lumpsumProfit),
      lumpsumTaxableGains: Math.round(lumpsumTaxableGains),
      lumpsumLTCGTax: Math.round(lumpsumLTCGTax),
      lumpsumPostTaxValue: Math.round(lumpsumPostTaxValue),
    };
  }, [formData, currentEMI]);

  const handleAddAsGoal = () => {
    const goalData = {
      name: 'Home Loan Clearance',
      type: 'Lump Sum',
      timeHorizon: goalFormData.timeHorizon,
      goalAmount: formData.outstandingBalance,
      inflationRate: 0, // No inflation for loan amount
      expectedReturn: formData.mutualFundReturn,
      tagExistingInvestments: false,
      existingInvestmentValue: 0,
      sipContribution: goalFormData.investmentType === 'sip' ? calculations.sipRequired : 0,
      lumpSumContribution: 0,
      whatIfPercentage: 0, // No what-if for loan clearance

      // Custom fields for home loan
      isHomeLoan: true,
      loanDetails: {
        outstandingBalance: formData.outstandingBalance,
        interestRate: formData.interestRate,
        remainingTenure: formData.remainingTenure,
        currentEMI: calculations.currentEMI,
        requiredExtraEMI: calculations.requiredExtraPayment,
        investmentType: goalFormData.investmentType,
        sipStartYear: formData.sipStartYear,
        sipDuration: formData.sipDuration,
        outstandingAtTarget: calculations.outstandingAtTarget,
      }
    };

    if (onAddAsGoal) {
      onAddAsGoal(goalData);
      onClose();
    }
  };

  // Chart for balance over time
  const balanceChartOption = {
    tooltip: {
      trigger: 'axis',
      formatter: function (params) {
        const year = params[0].axisValue;
        return `Year ${year}<br/>
                Standard Balance: ${formatCurrency(params[0].value)}<br/>
                Accelerated Balance: ${formatCurrency(params[1].value)}`;
      }
    },
    legend: {
      data: ['Standard Repayment', 'Accelerated Repayment'],
      top: 25
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: calculations.standardSchedule.map(item => item.year),
      name: 'Year'
    },
    yAxis: {
      type: 'value',
      name: 'Outstanding Balance (₹)',
      axisLabel: {
        formatter: (value) => `₹${(value / 100000).toFixed(0)}L`
      }
    },
    series: [
      {
        name: 'Standard Repayment',
        type: 'line',
        data: calculations.standardSchedule.map(item => item.balance),
        itemStyle: { color: '#ef4444' },
        areaStyle: { color: 'rgba(239, 68, 68, 0.1)' }
      },
      {
        name: 'Accelerated Repayment',
        type: 'line',
        data: calculations.acceleratedSchedule.map(item => item.balance),
        itemStyle: { color: '#10b981' },
        areaStyle: { color: 'rgba(16, 185, 129, 0.1)' }
      }
    ]
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-7xl max-h-[95vh] overflow-y-auto my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-[#19b48d] flex items-center justify-center">
              <SafeIcon icon={FiHome} className="text-white text-sm" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Home Loan Repayment Calculator
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <SafeIcon icon={FiX} className="text-lg" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Input Section */}
            <div className="lg:col-span-1 space-y-6">
              {/* Loan Details */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Current Loan Details</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Outstanding Balance ₹
                    </label>
                    <input
                      type="number"
                      value={formData.outstandingBalance}
                      onChange={(e) => handleInputChange('outstandingBalance', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d] focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">{formatCurrency(formData.outstandingBalance)}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Interest Rate (% p.a.)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.interestRate}
                      onChange={(e) => handleInputChange('interestRate', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d] focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Remaining Tenure (Months)
                    </label>
                    <input
                      type="number"
                      value={formData.remainingTenure}
                      onChange={(e) => handleInputChange('remainingTenure', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19b48d] focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">{(formData.remainingTenure / 12).toFixed(1)} years</p>
                  </div>

                  {/* Auto-calculated Current EMI - Display Only */}
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <label className="block text-sm font-medium text-blue-800 mb-2">
                      Current Monthly EMI (Auto-calculated)
                    </label>
                    <p className="text-2xl font-bold text-blue-900">{formatCurrency(calculations.currentEMI)}</p>
                    <p className="text-xs text-blue-700 mt-1">
                      Based on outstanding balance, interest rate, and tenure
                    </p>
                  </div>
                </div>
              </div>

              {/* Repayment Strategy */}
              <div className="bg-orange-50 rounded-xl shadow-sm border border-orange-200 p-6">
                <h4 className="text-md font-semibold text-orange-900 mb-4">Repayment Strategy</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-orange-800 mb-2">
                      Target to Clear in (Years)
                    </label>
                    <input
                      type="number"
                      value={formData.targetYears}
                      onChange={(e) => handleInputChange('targetYears', e.target.value)}
                      className="w-full px-4 py-3 border border-orange-300 bg-white rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                      min="1"
                    />
                  </div>
                </div>
              </div>

              {/* SIP Investment Strategy */}
              <div className="bg-green-50 rounded-xl shadow-sm border border-green-200 p-6">
                <h4 className="text-md font-semibold text-green-900 mb-4">SIP Investment Strategy</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-green-800 mb-2">
                      Start SIP After (Years)
                    </label>
                    <input
                      type="number"
                      value={formData.sipStartYear}
                      onChange={(e) => handleInputChange('sipStartYear', e.target.value)}
                      className="w-full px-4 py-3 border border-green-300 bg-white rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent"
                      min="0"
                      max={formData.targetYears}
                    />
                    <p className="text-xs text-green-700 mt-1">
                      Outstanding at Year {formData.sipStartYear}: {formatCurrency(calculations.outstandingAtSIPStart)}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-green-800 mb-2">
                      SIP Investment Duration (Years)
                    </label>
                    <input
                      type="number"
                      value={formData.sipDuration}
                      onChange={(e) => handleInputChange('sipDuration', e.target.value)}
                      className="w-full px-4 py-3 border border-green-300 bg-white rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent"
                      min="1"
                      max={formData.targetYears - formData.sipStartYear}
                    />
                    <p className="text-xs text-green-700 mt-1">
                      Target Year {formData.sipStartYear + formData.sipDuration}: {formatCurrency(calculations.outstandingAtTarget)} outstanding
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-green-800 mb-2">
                      Expected Mutual Fund Returns (% p.a.)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={formData.mutualFundReturn}
                      onChange={(e) => handleInputChange('mutualFundReturn', e.target.value)}
                      className="w-full px-4 py-3 border border-green-300 bg-white rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent"
                      min="0"
                      max="30"
                    />
                  </div>
                </div>
              </div>

              {/* Add as Goal Button */}
              <button
                onClick={() => setShowAddGoal(!showAddGoal)}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-[#19b48d] text-white rounded-lg hover:bg-[#16a085] transition-colors duration-200"
              >
                <SafeIcon icon={FiTarget} className="text-lg" />
                <span>Add as Financial Goal</span>
              </button>

              {/* Add Goal Form */}
              {showAddGoal && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl shadow-sm border border-green-200 p-6"
                >
                  <h4 className="text-md font-semibold text-green-900 mb-4">Add as Goal</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-green-800 mb-2">
                        Investment Strategy
                      </label>
                      <div className="space-y-2">
                        <label className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-green-200 cursor-pointer hover:bg-green-50">
                          <input
                            type="radio"
                            name="investmentType"
                            value="sip"
                            checked={goalFormData.investmentType === 'sip'}
                            onChange={(e) => handleGoalFormChange('investmentType', e.target.value)}
                            className="text-[#19b48d] focus:ring-[#19b48d]"
                          />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">SIP Investment</p>
                            <p className="text-xs text-gray-600">
                              Invest {formatCurrency(calculations.sipRequired)}/month for {formData.sipDuration} years
                            </p>
                          </div>
                        </label>

                        <label className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-green-200 cursor-pointer hover:bg-green-50">
                          <input
                            type="radio"
                            name="investmentType"
                            value="extraEMI"
                            checked={goalFormData.investmentType === 'extraEMI'}
                            onChange={(e) => handleGoalFormChange('investmentType', e.target.value)}
                            className="text-[#19b48d] focus:ring-[#19b48d]"
                          />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">Extra EMI Payment</p>
                            <p className="text-xs text-gray-600">
                              Pay additional {formatCurrency(calculations.requiredExtraPayment)}/month as EMI
                            </p>
                          </div>
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-green-800 mb-2">
                        Time Period (Years)
                      </label>
                      <input
                        type="number"
                        value={goalFormData.timeHorizon}
                        onChange={(e) => handleGoalFormChange('timeHorizon', parseInt(e.target.value))}
                        className="w-full px-4 py-3 border border-green-300 bg-white rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent"
                        min="1"
                        max={Math.ceil(formData.remainingTenure / 12)}
                      />
                    </div>

                    <div className="p-4 bg-white rounded-lg border border-green-200">
                      <h5 className="text-sm font-medium text-green-800 mb-2">Goal Summary</h5>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Goal Name:</span>
                          <span className="font-medium text-gray-900">Home Loan Clearance</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Target Amount:</span>
                          <span className="font-medium text-gray-900">{formatCurrency(calculations.outstandingAtTarget)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">SIP Start:</span>
                          <span className="font-medium text-gray-900">After {formData.sipStartYear} years</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">SIP Duration:</span>
                          <span className="font-medium text-gray-900">{formData.sipDuration} years</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Strategy:</span>
                          <span className="font-medium text-gray-900">
                            {goalFormData.investmentType === 'sip' ? 'SIP Investment' : 'Extra EMI Payment'}
                          </span>
                        </div>
                        {goalFormData.investmentType === 'sip' && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Monthly SIP:</span>
                            <span className="font-medium text-green-700">{formatCurrency(calculations.sipRequired)}</span>
                          </div>
                        )}
                        {goalFormData.investmentType === 'extraEMI' && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Extra EMI:</span>
                            <span className="font-medium text-green-700">{formatCurrency(calculations.requiredExtraPayment)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={handleAddAsGoal}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
                    >
                      <SafeIcon icon={FiCheck} className="text-lg" />
                      <span>Confirm & Add Goal</span>
                    </button>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Results Section */}
            <div className="lg:col-span-2 space-y-6">
              {/* SIP Investment Required - Main Output */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl shadow-sm border border-green-200 p-8">
                <div className="text-center">
                  <SafeIcon icon={FiDollarSign} className="text-green-600 text-5xl mx-auto mb-4" />
                  <h4 className="text-lg font-semibold text-green-900 mb-2">Required Monthly SIP</h4>
                  <p className="text-5xl font-bold text-green-900 mb-2">
                    {formatCurrency(calculations.sipRequired)}
                  </p>
                  <p className="text-sm text-green-700">
                    for {formData.sipDuration} years starting after {formData.sipStartYear} years
                  </p>
                </div>

                <div className="mt-6 pt-6 border-t border-green-200">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <p className="text-sm text-green-700 mb-1">Target Outstanding</p>
                      <p className="text-2xl font-bold text-green-900">{formatCurrency(calculations.outstandingAtTarget)}</p>
                      <p className="text-xs text-green-600">After {formData.sipStartYear + formData.sipDuration} years</p>
                    </div>
                    <div>
                      <p className="text-sm text-green-700 mb-1">Total Investment</p>
                      <p className="text-2xl font-bold text-green-900">{formatCurrency(calculations.totalSIPInvestment)}</p>
                      <p className="text-xs text-green-600">Over {formData.sipDuration} years</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-green-200">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <p className="text-sm text-green-700 mb-1">Returns Generated</p>
                      <p className="text-2xl font-bold text-green-900">{formatCurrency(calculations.sipReturns)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-green-700 mb-1">Lumpsum Alternative</p>
                      <p className="text-2xl font-bold text-green-900">{formatCurrency(calculations.lumpsumRequired)}</p>
                      <p className="text-xs text-green-600">One-time today</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* LTCG Tax Analysis - NEW SECTION */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">LTCG Tax Analysis (Investment Route)</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* SIP Route Tax */}
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h5 className="text-sm font-semibold text-blue-900 mb-3">SIP Investment Tax</h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Investment:</span>
                        <span className="font-medium text-gray-900">{formatCurrency(calculations.totalSIPInvestment)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Future Value:</span>
                        <span className="font-medium text-gray-900">{formatCurrency(calculations.outstandingAtTarget)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Profit:</span>
                        <span className="font-medium text-green-700">{formatCurrency(calculations.sipProfit)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Exemption:</span>
                        <span className="font-medium text-blue-700">₹1,25,000</span>
                      </div>
                      <div className="flex justify-between border-t border-blue-300 pt-2">
                        <span className="text-gray-600">Taxable Gains:</span>
                        <span className="font-medium text-gray-900">{formatCurrency(calculations.sipTaxableGains)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">LTCG Tax (12.5%):</span>
                        <span className="font-medium text-red-700">{formatCurrency(calculations.sipLTCGTax)}</span>
                      </div>
                      <div className="flex justify-between border-t border-blue-300 pt-2">
                        <span className="text-gray-700 font-medium">Post-Tax Value:</span>
                        <span className="font-bold text-blue-900">{formatCurrency(calculations.sipPostTaxValue)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Lumpsum Route Tax */}
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <h5 className="text-sm font-semibold text-purple-900 mb-3">Lumpsum Investment Tax</h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Investment:</span>
                        <span className="font-medium text-gray-900">{formatCurrency(calculations.lumpsumRequired)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Future Value:</span>
                        <span className="font-medium text-gray-900">{formatCurrency(calculations.outstandingAtTarget)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Profit:</span>
                        <span className="font-medium text-green-700">{formatCurrency(calculations.lumpsumProfit)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Exemption:</span>
                        <span className="font-medium text-purple-700">₹1,25,000</span>
                      </div>
                      <div className="flex justify-between border-t border-purple-300 pt-2">
                        <span className="text-gray-600">Taxable Gains:</span>
                        <span className="font-medium text-gray-900">{formatCurrency(calculations.lumpsumTaxableGains)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">LTCG Tax (12.5%):</span>
                        <span className="font-medium text-red-700">{formatCurrency(calculations.lumpsumLTCGTax)}</span>
                      </div>
                      <div className="flex justify-between border-t border-purple-300 pt-2">
                        <span className="text-gray-700 font-medium">Post-Tax Value:</span>
                        <span className="font-bold text-purple-900">{formatCurrency(calculations.lumpsumPostTaxValue)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-sm text-yellow-800">
                    <strong>LTCG Tax:</strong> 12.5% tax on profits above ₹1,25,000 exemption (for investments held &gt; 1 year in mutual funds/real estate)
                  </p>
                </div>
              </div>

              {/* Timeline Visualization */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Investment Timeline</h4>
                <div className="relative">
                  <div className="flex items-center justify-between mb-8">
                    <div className="text-center flex-1">
                      <div className="w-16 h-16 rounded-full bg-blue-100 border-4 border-blue-500 flex items-center justify-center mx-auto mb-2">
                        <SafeIcon icon={FiClock} className="text-blue-600 text-2xl" />
                      </div>
                      <p className="text-sm font-medium text-gray-900">Today</p>
                      <p className="text-xs text-gray-600">{formatCurrency(formData.outstandingBalance)}</p>
                    </div>

                    <div className="flex-1 h-1 bg-gradient-to-r from-blue-500 to-green-500"></div>

                    <div className="text-center flex-1">
                      <div className="w-16 h-16 rounded-full bg-green-100 border-4 border-green-500 flex items-center justify-center mx-auto mb-2">
                        <SafeIcon icon={FiTarget} className="text-green-600 text-2xl" />
                      </div>
                      <p className="text-sm font-medium text-gray-900">Start SIP</p>
                      <p className="text-xs text-gray-600">Year {formData.sipStartYear}</p>
                      <p className="text-xs text-gray-600">{formatCurrency(calculations.outstandingAtSIPStart)}</p>
                    </div>

                    <div className="flex-1 h-1 bg-gradient-to-r from-green-500 to-purple-500"></div>

                    <div className="text-center flex-1">
                      <div className="w-16 h-16 rounded-full bg-purple-100 border-4 border-purple-500 flex items-center justify-center mx-auto mb-2">
                        <SafeIcon icon={FiCheck} className="text-purple-600 text-2xl" />
                      </div>
                      <p className="text-sm font-medium text-gray-900">Clear Loan</p>
                      <p className="text-xs text-gray-600">Year {formData.sipStartYear + formData.sipDuration}</p>
                      <p className="text-xs text-gray-600">{formatCurrency(calculations.outstandingAtTarget)}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-6">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-700 font-medium">Pay EMI Only</p>
                    <p className="text-sm text-blue-900">{formData.sipStartYear} years</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-xs text-green-700 font-medium">SIP Investment</p>
                    <p className="text-sm text-green-900">{formData.sipDuration} years</p>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <p className="text-xs text-purple-700 font-medium">Total Timeline</p>
                    <p className="text-sm text-purple-900">{formData.sipStartYear + formData.sipDuration} years</p>
                  </div>
                </div>
              </div>

              {/* Required Extra EMI (for comparison) */}
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl shadow-sm border border-orange-200 p-6">
                <h4 className="text-md font-semibold text-orange-900 mb-4">Alternative: Extra EMI Payment</h4>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="text-sm text-orange-700 mb-1">Extra EMI Required</p>
                    <p className="text-2xl font-bold text-orange-900">{formatCurrency(calculations.requiredExtraPayment)}</p>
                    <p className="text-xs text-orange-600">per month for {formData.targetYears} years</p>
                  </div>
                  <div>
                    <p className="text-sm text-orange-700 mb-1">New Total EMI</p>
                    <p className="text-2xl font-bold text-orange-900">
                      {formatCurrency(calculations.currentEMI + calculations.requiredExtraPayment)}
                    </p>
                    <p className="text-xs text-orange-600">Current: {formatCurrency(calculations.currentEMI)}</p>
                  </div>
                </div>
              </div>

              {/* Savings Analysis */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl shadow-sm border border-green-200 p-6">
                <h4 className="text-md font-semibold text-green-900 mb-4">Savings with Accelerated Repayment</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-sm text-green-700 mb-1">Interest Saved</p>
                    <p className="text-2xl font-bold text-green-900">{formatCurrency(calculations.interestSaved)}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-green-700 mb-1">Time Saved</p>
                    <p className="text-2xl font-bold text-green-900">{(calculations.tenureSaved / 12).toFixed(1)} years</p>
                    <p className="text-xs text-green-700">{calculations.tenureSaved} months</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-green-700 mb-1">Total Saved</p>
                    <p className="text-2xl font-bold text-green-900">{formatCurrency(calculations.totalSaved)}</p>
                  </div>
                </div>
              </div>

              {/* Outstanding Balance Chart */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Outstanding Balance Over Time</h4>
                <div className="h-80">
                  <ReactECharts option={balanceChartOption} style={{ height: '100%', width: '100%' }} />
                </div>
              </div>

              {/* Key Insights */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200 p-6">
                <h4 className="text-md font-semibold text-gray-900 mb-4">Key Insights</h4>
                <div className="space-y-3">
                  <div className="flex items-start space-x-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <p className="text-sm text-gray-700">
                      <strong>SIP Strategy:</strong> Start investing {formatCurrency(calculations.sipRequired)}/month after {formData.sipStartYear} years, continue for {formData.sipDuration} years to accumulate {formatCurrency(calculations.outstandingAtTarget)} to clear the loan.
                    </p>
                  </div>

                  <div className="flex items-start space-x-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <p className="text-sm text-gray-700">
                      <strong>Outstanding Reduction:</strong> By Year {formData.sipStartYear}, your outstanding will reduce from {formatCurrency(formData.outstandingBalance)} to {formatCurrency(calculations.outstandingAtSIPStart)} through regular EMI payments.
                    </p>
                  </div>

                  <div className="flex items-start space-x-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <p className="text-sm text-gray-700">
                      <strong>Target Outstanding:</strong> By Year {formData.sipStartYear + formData.sipDuration}, the outstanding will be {formatCurrency(calculations.outstandingAtTarget)}, which your SIP investment will cover.
                    </p>
                  </div>

                  <div className="flex items-start space-x-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <p className="text-sm text-gray-700">
                      <strong>Investment Returns:</strong> Your total investment of {formatCurrency(calculations.totalSIPInvestment)} will generate returns of {formatCurrency(calculations.sipReturns)} at {formData.mutualFundReturn}% annual returns.
                    </p>
                  </div>

                  <div className="flex items-start space-x-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <p className="text-sm text-gray-700">
                      <strong>LTCG Tax Impact:</strong> After {formData.sipDuration} years (&gt;1 year holding), you'll pay {formatCurrency(calculations.sipLTCGTax)} in LTCG tax (12.5% on profits above ₹1.25L exemption). Post-tax value: {formatCurrency(calculations.sipPostTaxValue)}.
                    </p>
                  </div>

                  <div className="flex items-start space-x-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <p className="text-sm text-gray-700">
                      <strong>Lumpsum Alternative:</strong> Instead of SIP, you can invest {formatCurrency(calculations.lumpsumRequired)} as a one-time lumpsum today to reach the same target amount. LTCG tax: {formatCurrency(calculations.lumpsumLTCGTax)}.
                    </p>
                  </div>

                  <div className="flex items-start space-x-2">
                    <span className="text-indigo-600 mt-1">•</span>
                    <p className="text-sm text-gray-700">
                      <strong>EMI vs Investment:</strong> Alternatively, pay an extra {formatCurrency(calculations.requiredExtraPayment)}/month as EMI to clear the loan in {formData.targetYears} years and save {formatCurrency(calculations.interestSaved)} in interest.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default HomeLoanCalculator;